import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, Button, Modal, Input } from '@/components/ui';
import { ArrowLeft, User, LogOut, Trash2 } from 'lucide-react';

export function AccountPage() {
  const { isAuthenticated, dbUser, signOut, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await deleteAccount();
      navigate('/');
    } catch {
      setIsDeleting(false);
    }
  };

  // Redirect if not authenticated
  if (!isAuthenticated || !dbUser) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] p-4">
        <div className="mx-auto max-w-lg">
          <Link
            to="/settings"
            className="mb-6 flex items-center gap-2 text-sm text-[var(--color-muted)] hover:text-[var(--color-text)]"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to settings
          </Link>
          <p className="text-[var(--color-muted)]">Sign in to manage your account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4">
      <div className="mx-auto max-w-lg">
        {/* Back link */}
        <Link
          to="/settings"
          className="mb-6 flex items-center gap-2 text-sm text-[var(--color-muted)] hover:text-[var(--color-text)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to settings
        </Link>

        <h1 className="mb-6 text-2xl font-bold">Account</h1>

        <div className="space-y-4">
          {/* Account info */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-primary/10">
                  <User className="h-6 w-6 text-accent-primary" />
                </div>
                <div>
                  <p className="font-medium">{dbUser.name || 'User'}</p>
                  <p className="text-sm text-[var(--color-muted)]">{dbUser.email}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sign out */}
          <Card>
            <CardContent className="p-4">
              <Button
                variant="danger"
                className="w-full"
                onClick={handleSignOut}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </Button>
            </CardContent>
          </Card>

          {/* Delete account */}
          <Card>
            <CardContent className="p-4">
              <p className="mb-3 text-sm text-[var(--color-muted)]">
                Permanently delete your account and all associated data including favorites,
                hidden bars, and submissions. This cannot be undone.
              </p>
              <Button
                variant="ghost"
                className="w-full text-red-500 hover:text-red-400"
                onClick={() => setShowDeleteModal(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Account
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setConfirmText('');
        }}
        title="Delete Account"
        description="This action is permanent and cannot be undone. All your data will be deleted."
      >
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-muted)]">
            Type <span className="font-mono font-semibold text-red-500">DELETE</span> to confirm.
          </p>
          <Input
            type="text"
            placeholder="Type DELETE to confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoFocus
          />
          <div className="flex gap-3">
            <Button
              variant="ghost"
              className="flex-1"
              onClick={() => {
                setShowDeleteModal(false);
                setConfirmText('');
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              disabled={confirmText !== 'DELETE' || isDeleting}
              isLoading={isDeleting}
              onClick={handleDeleteAccount}
            >
              Delete My Account
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
